<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { AdminPersonRecord } from '../../lib/types';
  import { upsertAdminTicket } from '../../lib/convexApi';

export interface GuestPayload {
  ticketId?: string | null;
  name: string;
  email?: string | null;
  personId?: string | null;
  emailLocked?: boolean;
  redeemed: boolean;
}

  export let open = false;
  export let eventId: string | null = null;
  export let eventName: string | null = null;
  export let initial: GuestPayload | null = null;
  export let people: AdminPersonRecord[] = [];
  export let existingPersonIds: string[] = [];

  const dispatch = createEventDispatcher<{ close: void; saved: void }>();

  let name = '';
  let email = '';
  let redeemed = false;
  let saving = false;
  let error: string | null = null;
  let selectedPersonId: string | null = null;
  let emailLocked = false;
  let searchTerm = '';

  let lastSnapshot = '';

  $: {
    const snapshot = open
      ? JSON.stringify({
          ticketId: initial?.ticketId ?? null,
          name: initial?.name ?? '',
          email: initial?.email ?? '',
          personId: initial?.personId ?? null,
          redeemed: initial?.redeemed ?? false,
          emailLocked: initial?.emailLocked ?? false
        })
      : '';

    if (snapshot !== lastSnapshot) {
      lastSnapshot = snapshot;
      if (open) {
        name = initial?.name ?? '';
        email = initial?.email ?? '';
        redeemed = initial?.redeemed ?? false;
        selectedPersonId = initial?.personId ?? null;
        emailLocked = Boolean(initial?.emailLocked);
        searchTerm = initial?.name ?? '';
        error = null;
      }
    }
  }

  $: suggestions = searchTerm.trim().length > 1
    ? people
        .filter(person => {
          const term = searchTerm.trim().toLowerCase();
          return (
            person.person.name.toLowerCase().includes(term) ||
            (person.person.email ?? '').toLowerCase().includes(term)
          );
        })
        .slice(0, 6)
    : [];

  function selectPerson(person: AdminPersonRecord) {
    selectedPersonId = person.person.id;
    name = person.person.name;
    email = person.person.email ?? '';
    emailLocked = Boolean(person.person.externalId);
    searchTerm = '';
  }

  function clearPersonSelection() {
    selectedPersonId = null;
    emailLocked = false;
    searchTerm = '';
  }

  function handleNameInput(event: Event) {
    const value = (event.currentTarget as HTMLInputElement).value;
    name = value;
    if (selectedPersonId) clearPersonSelection();
  }

  function handleEmailInput(event: Event) {
    const value = (event.currentTarget as HTMLInputElement).value;
    email = value;
    if (selectedPersonId && !emailLocked) {
      clearPersonSelection();
    }
  }

  function handleSearchInput(event: Event) {
    const value = (event.currentTarget as HTMLInputElement).value;
    searchTerm = value;
  }

  function close() {
    dispatch('close');
  }

  async function save() {
    if (!eventId) {
      error = 'Missing event identifier';
      return;
    }
    if (!name.trim()) {
      error = 'Name is required';
      return;
    }

    const selectedId = selectedPersonId;
    if (
      selectedId &&
      existingPersonIds.includes(selectedId) &&
      !initial?.ticketId
    ) {
      error = 'This person is already on the guestlist.';
      return;
    }
    if (
      selectedId &&
      initial?.personId &&
      selectedId !== initial.personId &&
      existingPersonIds.includes(selectedId)
    ) {
      error = 'This person is already on the guestlist.';
      return;
    }

    saving = true;
    error = null;
    try {
      const payload: Parameters<typeof upsertAdminTicket>[0] = {
        ticketId: initial?.ticketId,
        eventId,
        redeemed
      };

      if (selectedId) {
        payload.personId = selectedId;
      } else {
        payload.person = {
          name: name.trim(),
          email: email.trim() || undefined
        };
      }

      await upsertAdminTicket(payload);
      dispatch('saved');
      close();
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to save guest';
    } finally {
      saving = false;
    }
  }
</script>

{#if open}
  <dialog class="guest-editor" open>
    <div class="modal-card">
      <header>
        <div>
          <h3>{initial ? 'Edit guest' : 'Add guest'}</h3>
          {#if eventName}
            <p class="muted">{eventName}</p>
          {/if}
        </div>
        <button type="button" class="ghost" on:click={close}>Close</button>
      </header>

      <form on:submit|preventDefault={save}>
        <div class="person-picker">
          <span>Person</span>
          <input
            type="text"
            placeholder="Search existing person"
            bind:value={searchTerm}
            on:input={handleSearchInput}
          />
          {#if selectedPersonId}
            <p class="muted">Linked to {name}. <button type="button" class="link" on:click={clearPersonSelection}>Clear selection</button></p>
          {/if}
          {#if suggestions.length > 0 && !selectedPersonId}
            <ul class="suggestions">
              {#each suggestions as person}
                <li>
                  <button type="button" on:click={() => selectPerson(person)}>
                    <strong>{person.person.name}</strong>
                    {#if person.person.email}
                      <span>{person.person.email}</span>
                    {/if}
                  </button>
                </li>
              {/each}
            </ul>
          {/if}
        </div>

        <label>
          <span>Name</span>
          <input
            type="text"
            value={name}
            on:input={handleNameInput}
            placeholder="Jane Doe"
            required
          />
        </label>
        <label class:disabled={emailLocked}>
          <span>Email {emailLocked ? '(managed by Clerk)' : ''}</span>
          <input
            type="email"
            value={email}
            placeholder="jane@example.com"
            disabled={emailLocked}
            on:input={handleEmailInput}
          />
        </label>
        <label class="toggle">
          <input type="checkbox" bind:checked={redeemed} />
          <span>Mark as redeemed</span>
        </label>

        {#if error}
          <p class="error">{error}</p>
        {/if}

        <footer>
          <button type="button" class="ghost" on:click={close}>Cancel</button>
          <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save guest'}</button>
        </footer>
      </form>
    </div>
  </dialog>
{/if}

<style>
  dialog.guest-editor {
    position: fixed;
    inset: 0;
    margin: auto;
    background: rgba(7, 8, 12, 0.75);
    border: none;
    width: min(520px, 90vw);
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-card {
    width: 100%;
    padding: var(--space-5);
    border-radius: var(--radius-xl);
    background: linear-gradient(180deg, rgba(16, 18, 26, 0.92), rgba(16, 18, 26, 0.85));
    border: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-4);
  }

  header h3 {
    margin: 0;
  }

  header .muted {
    margin: 0.2rem 0 0;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.85rem;
  }

  input[type='text'],
  input[type='email'] {
    padding: 0.6rem 0.75rem;
    border-radius: var(--radius-md);
    border: 1px solid transparent;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
  }

  label.disabled input {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .person-picker {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .person-picker .suggestions {
    list-style: none;
    margin: 0;
    padding: 0;
    border-radius: var(--radius-md);
    overflow: hidden;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .person-picker .suggestions li + li {
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  .person-picker .suggestions button {
    width: 100%;
    text-align: left;
    padding: 0.55rem 0.75rem;
    background: transparent;
    border: none;
    cursor: pointer;
    color: inherit;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .person-picker .suggestions button:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .person-picker .link {
    border: none;
    background: none;
    color: var(--color-text-primary);
    cursor: pointer;
    text-decoration: underline;
    font-size: 0.8rem;
  }

  .toggle {
    flex-direction: row;
    align-items: center;
    gap: 0.6rem;
  }

  .error {
    margin: 0;
    color: #ff9a9a;
  }

  footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-3);
    margin-top: var(--space-2);
  }

  button {
    border: none;
    border-radius: var(--radius-lg);
    padding: 0.55rem 1rem;
    cursor: pointer;
    font-weight: 600;
  }

  button.ghost {
    background: rgba(255, 255, 255, 0.12);
    color: inherit;
  }

  button:not(.ghost) {
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.6), rgba(178, 127, 244, 0.4));
    color: #050608;
  }

  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
